/**
 * Robust storage utility with fallback mechanisms
 * Handles localStorage issues, service worker conflicts, and provides multiple storage options
 */

import { logger } from './logger';

export interface StorageOptions {
  useSessionStorage?: boolean;
  useCookies?: boolean;
  useIndexedDB?: boolean;
  maxRetries?: number;
}

class StorageManager {
  private static instance: StorageManager;
  private isServiceWorkerContext: boolean = false;
  private dbPromise: Promise<IDBDatabase> | null = null;

  private constructor() {
    // Detect if running in service worker context
    this.isServiceWorkerContext = typeof (globalThis as any).importScripts === 'function';
    
    if (!this.isServiceWorkerContext && typeof window !== 'undefined') {
      this.initIndexedDB();
    }
  }

  static getInstance(): StorageManager {
    if (!StorageManager.instance) {
      StorageManager.instance = new StorageManager();
    }
    return StorageManager.instance;
  }

  private async initIndexedDB(): Promise<void> {
    if (!window.indexedDB) return;

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open('AppStateDB', 1);
      
      request.onerror = () => {
        logger.error('StorageManager', 'Failed to open IndexedDB', request.error);
        reject(request.error);
      };
      
      request.onsuccess = () => {
        resolve(request.result);
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('state')) {
          db.createObjectStore('state', { keyPath: 'key' });
        }
      };
    });
  }

  async setItem(key: string, value: string, options: StorageOptions = {}): Promise<boolean> {
    const { useSessionStorage = false, useCookies = false, useIndexedDB = true, maxRetries = 3 } = options;
    
    let success = false;
    
    // Try localStorage first (most common)
    if (!this.isServiceWorkerContext && typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem(key, value);
        logger.debug('StorageManager', `Saved ${key} to localStorage`);
        success = true;
      } catch (error) {
        logger.warn('StorageManager', `localStorage failed for ${key}`, error);
        
        // If quota exceeded, try to clear some space
        if (error instanceof Error && error.name === 'QuotaExceededError') {
          await this.clearOldData();
          
          // Retry after clearing
          try {
            window.localStorage.setItem(key, value);
            success = true;
            logger.info('StorageManager', `Saved ${key} to localStorage after clearing space`);
          } catch (retryError) {
            logger.error('StorageManager', `localStorage retry failed for ${key}`, retryError);
          }
        }
      }
    }

    // Fallback to sessionStorage
    if (!success && useSessionStorage && typeof window !== 'undefined' && window.sessionStorage) {
      try {
        window.sessionStorage.setItem(key, value);
        logger.debug('StorageManager', `Saved ${key} to sessionStorage`);
        success = true;
      } catch (error) {
        logger.warn('StorageManager', `sessionStorage failed for ${key}`, error);
      }
    }

    // Fallback to IndexedDB
    if (!success && useIndexedDB && this.dbPromise) {
      try {
        const db = await this.dbPromise;
        const transaction = db.transaction(['state'], 'readwrite');
        const store = transaction.objectStore('state');
        
        await new Promise((resolve, reject) => {
          const request = store.put({ key, value, timestamp: Date.now() });
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });
        
        logger.debug('StorageManager', `Saved ${key} to IndexedDB`);
        success = true;
      } catch (error) {
        logger.warn('StorageManager', `IndexedDB failed for ${key}`, error);
      }
    }

    // Fallback to cookies (limited storage)
    if (!success && useCookies && typeof document !== 'undefined') {
      try {
        // Only use cookies for small data (< 2KB)
        if (value.length < 2048) {
          const expires = new Date();
          expires.setDate(expires.getDate() + 7); // 7 days
          document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Strict`;
          logger.debug('StorageManager', `Saved ${key} to cookies`);
          success = true;
        }
      } catch (error) {
        logger.warn('StorageManager', `Cookie storage failed for ${key}`, error);
      }
    }

    if (!success) {
      logger.error('StorageManager', `All storage methods failed for ${key}`);
    }

    return success;
  }

  async getItem(key: string, options: StorageOptions = {}): Promise<string | null> {
    const { useSessionStorage = false, useCookies = false, useIndexedDB = true } = options;
    
    // Try localStorage first
    if (!this.isServiceWorkerContext && typeof window !== 'undefined' && window.localStorage) {
      try {
        const value = window.localStorage.getItem(key);
        if (value !== null) {
          logger.debug('StorageManager', `Retrieved ${key} from localStorage`);
          return value;
        }
      } catch (error) {
        logger.warn('StorageManager', `localStorage read failed for ${key}`, error);
      }
    }

    // Try sessionStorage
    if (useSessionStorage && typeof window !== 'undefined' && window.sessionStorage) {
      try {
        const value = window.sessionStorage.getItem(key);
        if (value !== null) {
          logger.debug('StorageManager', `Retrieved ${key} from sessionStorage`);
          return value;
        }
      } catch (error) {
        logger.warn('StorageManager', `sessionStorage read failed for ${key}`, error);
      }
    }

    // Try IndexedDB
    if (useIndexedDB && this.dbPromise) {
      try {
        const db = await this.dbPromise;
        const transaction = db.transaction(['state'], 'readonly');
        const store = transaction.objectStore('state');
        
        const result = await new Promise<any>((resolve, reject) => {
          const request = store.get(key);
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });
        
        if (result && result.value) {
          logger.debug('StorageManager', `Retrieved ${key} from IndexedDB`);
          return result.value;
        }
      } catch (error) {
        logger.warn('StorageManager', `IndexedDB read failed for ${key}`, error);
      }
    }

    // Try cookies
    if (useCookies && typeof document !== 'undefined') {
      try {
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
          const [cookieKey, cookieValue] = cookie.trim().split('=');
          if (decodeURIComponent(cookieKey) === key) {
            const value = decodeURIComponent(cookieValue);
            logger.debug('StorageManager', `Retrieved ${key} from cookies`);
            return value;
          }
        }
      } catch (error) {
        logger.warn('StorageManager', `Cookie read failed for ${key}`, error);
      }
    }

    logger.debug('StorageManager', `No value found for ${key} in any storage`);
    return null;
  }

  async removeItem(key: string, options: StorageOptions = {}): Promise<void> {
    const { useSessionStorage = false, useCookies = false, useIndexedDB = true } = options;

    // Remove from localStorage
    if (!this.isServiceWorkerContext && typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.removeItem(key);
      } catch (error) {
        logger.warn('StorageManager', `localStorage remove failed for ${key}`, error);
      }
    }

    // Remove from sessionStorage
    if (useSessionStorage && typeof window !== 'undefined' && window.sessionStorage) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (error) {
        logger.warn('StorageManager', `sessionStorage remove failed for ${key}`, error);
      }
    }

    // Remove from IndexedDB
    if (useIndexedDB && this.dbPromise) {
      try {
        const db = await this.dbPromise;
        const transaction = db.transaction(['state'], 'readwrite');
        const store = transaction.objectStore('state');
        
        await new Promise((resolve, reject) => {
          const request = store.delete(key);
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });
      } catch (error) {
        logger.warn('StorageManager', `IndexedDB remove failed for ${key}`, error);
      }
    }

    // Remove from cookies
    if (useCookies && typeof document !== 'undefined') {
      try {
        document.cookie = `${encodeURIComponent(key)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      } catch (error) {
        logger.warn('StorageManager', `Cookie remove failed for ${key}`, error);
      }
    }
  }

  private async clearOldData(): Promise<void> {
    // Clear old error logs and other non-critical data
    const keysToRemove = ['app_errors', 'old_cache_data'];
    
    for (const key of keysToRemove) {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.removeItem(key);
        }
      } catch (error) {
        // Ignore errors during cleanup
      }
    }

    logger.info('StorageManager', 'Cleared old data to free up storage space');
  }

  async isStorageAvailable(): Promise<{localStorage: boolean, sessionStorage: boolean, indexedDB: boolean, cookies: boolean}> {
    const result = {
      localStorage: false,
      sessionStorage: false,
      indexedDB: false,
      cookies: false
    };

    // Test localStorage
    if (!this.isServiceWorkerContext && typeof window !== 'undefined' && window.localStorage) {
      try {
        const testKey = '__storage_test__';
        window.localStorage.setItem(testKey, 'test');
        window.localStorage.removeItem(testKey);
        result.localStorage = true;
      } catch (error) {
        // localStorage not available
      }
    }

    // Test sessionStorage
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        const testKey = '__storage_test__';
        window.sessionStorage.setItem(testKey, 'test');
        window.sessionStorage.removeItem(testKey);
        result.sessionStorage = true;
      } catch (error) {
        // sessionStorage not available
      }
    }

    // Test IndexedDB
    if (this.dbPromise) {
      try {
        await this.dbPromise;
        result.indexedDB = true;
      } catch (error) {
        // IndexedDB not available
      }
    }

    // Test cookies
    if (typeof document !== 'undefined') {
      try {
        document.cookie = '__test_cookie__=test; path=/';
        result.cookies = document.cookie.includes('__test_cookie__=test');
        document.cookie = '__test_cookie__=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
      } catch (error) {
        // Cookies not available
      }
    }

    return result;
  }
}

// Export singleton instance
export const storage = StorageManager.getInstance();